import Radar from "radar-sdk-js";
import "radar-sdk-js/dist/radar.css";
import { useCallback, useState } from "react";
import { getUser } from "../utils";

// State variables
let isInitialized = false;
let userId = null;
let metadata = null;
let description = null;

/**
 * Initialize Radar SDK with publishable API key from environment
 * @param {Object} options - Optional configuration options
 */
export const initializeRadar = (options = {}) => {
	const publishableKey = window.RADAR_PUBLISHABLE_KEY;

	if (!publishableKey) {
		throw new Error("RADAR_PUBLISHABLE_KEY is not defined in environment");
	}

	Radar.initialize(publishableKey, options);
	isInitialized = true;
};

/**
 * Set user ID for tracking
 * @param {string} newUserId - Unique user identifier
 */
export const setUserId = (newUserId) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	Radar.setUserId(newUserId);
	userId = newUserId;
};

/**
 * Set user metadata
 * @param {Object} newMetadata - User metadata object (max 16 keys)
 */
export const setMetadata = (newMetadata) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	if (!newMetadata || typeof newMetadata !== "object") {
		throw new Error("Metadata must be a valid object");
	}

	Radar.setMetadata(newMetadata);
	metadata = newMetadata;
};

/**
 * Set user description
 * @param {string} newDescription - User description for dashboard
 */
export const setDescription = (newDescription) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	Radar.setDescription(newDescription);
	description = newDescription;
};

/**
 * Track user location once
 * @param {Object} options - Optional location coordinates
 * @returns {Promise} Promise with location, user, and events
 */
export const trackOnce = async (options = null) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	try {
		// If options is null/undefined, pass an empty object instead
		const trackOptions = options || {};

		// Get user data from localStorage
		const userData = getUser();
		const username = userData?.username || "anonymous";

		// Add tenant and username information to tracking options
		const enhancedOptions = {
			...trackOptions,
			metadata: {
				...trackOptions.metadata,
				tenant: window.TENANT || "unknown",
				username: username,
				timestamp: new Date().toISOString(),
				userAgent: navigator.userAgent,
				url: window.location.href,
			},
		};

		const result = await Radar.trackOnce(enhancedOptions);
		return result;
	} catch (error) {
		// Handle specific Radar SDK errors
		if (error.name === "RadarLocationPermissionsError") {
			throw new Error("Location permissions not granted. Please enable location access.");
		} else if (error.name === "RadarLocationError") {
			throw new Error("Location services error. Please check your device location settings.");
		} else if (error.name === "RadarTimeoutError") {
			throw new Error("Location request timed out. Please try again.");
		} else if (error.name === "RadarPublishableKeyError") {
			throw new Error("Radar SDK not properly initialized.");
		}

		throw error;
	}
};

/**
 * Get current location without tracking
 * @returns {Promise} Promise with location data
 */
export const getLocation = async () => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.getLocation();
	return result;
};

/**
 * Get context for a location without tracking
 * @returns {Promise} Promise with location context
 */
export const getContext = async () => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.getContext();
	return result;
};

/**
 * Forward geocode address to coordinates
 * @param {Object} params - Geocoding parameters
 * @param {string} params.query - Address query
 * @returns {Promise} Promise with geocoded addresses
 */
export const forwardGeocode = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.forwardGeocode(params);
	return result;
};

/**
 * Reverse geocode coordinates to address
 * @param {Object} params - Reverse geocoding parameters
 * @param {number} params.latitude - Latitude coordinate
 * @param {number} params.longitude - Longitude coordinate
 * @returns {Promise} Promise with geocoded addresses
 */
export const reverseGeocode = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.reverseGeocode(params);
	return result;
};

/**
 * IP geocoding
 * @returns {Promise} Promise with IP geocoding result
 */
export const ipGeocode = async () => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.ipGeocode();
	return result;
};

/**
 * Address autocomplete
 * @param {Object} params - Autocomplete parameters
 * @param {string} params.query - Partial address query
 * @param {Object} params.near - Optional nearby location
 * @param {number} params.limit - Maximum results (default 10)
 * @returns {Promise} Promise with autocomplete results
 */
export const autocomplete = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.autocomplete(params);
	return result;
};

/**
 * Search geofences
 * @param {Object} params - Geofence search parameters
 * @param {number} params.radius - Search radius in meters
 * @param {Array} params.tags - Optional tags to filter by
 * @param {number} params.limit - Maximum results (default 10)
 * @returns {Promise} Promise with geofence search results
 */
export const searchGeofences = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.searchGeofences(params);
	return result;
};

/**
 * Search places
 * @param {Object} params - Place search parameters
 * @param {Object} params.near - Nearby location
 * @param {number} params.radius - Search radius in meters
 * @param {Array} params.chains - Optional chain names to filter by
 * @param {number} params.limit - Maximum results (default 10)
 * @returns {Promise} Promise with place search results
 */
export const searchPlaces = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.searchPlaces(params);
	return result;
};

/**
 * Validate address (US and Canada only)
 * @param {Object} params - Address validation parameters
 * @param {string} params.addressLabel - Street address
 * @param {string} params.city - City name
 * @param {string} params.stateCode - State/province code
 * @param {string} params.postalCode - Postal code
 * @param {string} params.countryCode - Country code
 * @returns {Promise} Promise with address validation result
 */
export const validateAddress = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.validateAddress(params);
	return result;
};

/**
 * Calculate distance between two locations
 * @param {Object} params - Distance calculation parameters
 * @param {Object} params.origin - Origin coordinates
 * @param {Object} params.destination - Destination coordinates
 * @param {Array} params.modes - Travel modes (foot, car, bike)
 * @param {string} params.units - Units (imperial or metric)
 * @returns {Promise} Promise with distance calculation result
 */
export const distance = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.distance(params);
	return result;
};

/**
 * Calculate matrix of distances between multiple origins and destinations
 * @param {Object} params - Matrix calculation parameters
 * @param {Array} params.origins - Array of origin coordinates
 * @param {Array} params.destinations - Array of destination coordinates
 * @param {string} params.mode - Travel mode
 * @param {string} params.units - Units (imperial or metric)
 * @returns {Promise} Promise with matrix calculation result
 */
export const matrix = async (params) => {
	if (!isInitialized) {
		throw new Error("Radar SDK must be initialized first");
	}

	const result = await Radar.matrix(params);
	return result;
};

/**
 * Check if Radar is initialized
 * @returns {boolean} Initialization status
 */
export const isRadarReady = () => {
	return isInitialized;
};

/**
 * Get current user ID
 * @returns {string|null} Current user ID
 */
export const getCurrentUserId = () => {
	return userId;
};

/**
 * Get current user metadata
 * @returns {Object|null} Current user metadata
 */
export const getCurrentMetadata = () => {
	return metadata;
};

/**
 * Get current user description
 * @returns {string|null} Current user description
 */
export const getCurrentDescription = () => {
	return description;
};

/**
 * Reset user data
 */
export const resetUser = () => {
	userId = null;
	metadata = null;
	description = null;
};

/**
 * Custom hook for Radar functionality
 * @returns {Object} Radar functions and state
 */
export const useRadar = () => {
	const [isReady, setIsReady] = useState(isInitialized);
	const [currentUserId, setCurrentUserId] = useState(userId);
	const [currentMetadata, setCurrentMetadata] = useState(metadata);
	const [currentDescription, setCurrentDescription] = useState(description);

	const initialize = useCallback((options = {}) => {
		initializeRadar(options);
		setIsReady(true);
	}, []);

	const setUser = useCallback((newUserId) => {
		setUserId(newUserId);
		setCurrentUserId(newUserId);
	}, []);

	const setUserMetadata = useCallback((newMetadata) => {
		setMetadata(newMetadata);
		setCurrentMetadata(newMetadata);
	}, []);

	const setUserDescription = useCallback((newDescription) => {
		setDescription(newDescription);
		setCurrentDescription(newDescription);
	}, []);

	const resetUserData = useCallback(() => {
		resetUser();
		setCurrentUserId(null);
		setCurrentMetadata(null);
		setCurrentDescription(null);
	}, []);

	return {
		// State
		isReady,
		currentUserId,
		currentMetadata,
		currentDescription,

		// Functions
		initialize,
		setUser,
		setUserMetadata,
		setUserDescription,
		trackOnce,
		getLocation,
		getContext,
		forwardGeocode,
		reverseGeocode,
		ipGeocode,
		autocomplete,
		searchGeofences,
		searchPlaces,
		validateAddress,
		distance,
		matrix,
		resetUserData,
	};
};

// Default export for backward compatibility
export default {
	initialize: initializeRadar,
	setUserId,
	setMetadata,
	setDescription,
	trackOnce,
	getLocation,
	getContext,
	forwardGeocode,
	reverseGeocode,
	ipGeocode,
	autocomplete,
	searchGeofences,
	searchPlaces,
	validateAddress,
	distance,
	matrix,
	isReady: isRadarReady,
	getCurrentUserId,
	getCurrentMetadata,
	getCurrentDescription,
	resetUser,
	useRadar,
};
